import { useState } from "react";
import { LocateFixed, MapPin, Search, Loader2, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ComuneInput, StreetInput } from "@/components/booking/AddressFields";
import { LocationPicker } from "@/components/booking/LocationPicker";
import { api } from "@/lib/api";

const Field = ({ id, label, children }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="text-sm font-semibold">{label}</Label>
    {children}
  </div>
);

function Consents({ form, set }) {
  const tog = (k) => (v) => set((p) => ({ ...p, [k]: !!v }));
  return (
    <div className="space-y-3 rounded-2xl border bg-white p-5 text-sm text-slate-700" data-testid="booking-consents">
      <label className="flex items-start gap-3">
        <Checkbox checked={!!form.privacy_accepted} onCheckedChange={tog("privacy_accepted")} data-testid="privacy-consent-checkbox" />
        <span>Ho letto l'<Link to="/privacy" target="_blank" className="font-semibold text-[#61a229] hover:underline" data-testid="privacy-consent-link">informativa privacy</Link> e i <Link to="/termini" target="_blank" className="font-semibold text-[#61a229] hover:underline" data-testid="terms-consent-link">termini del servizio</Link> *</span>
      </label>
      <label className="flex items-start gap-3">
        <Checkbox checked={!!form.marketing_consent} onCheckedChange={tog("marketing_consent")} data-testid="marketing-consent-checkbox" />
        <span>Acconsento a ricevere offerte e promozioni via WhatsApp ed email (facoltativo, revocabile in ogni momento)</span>
      </label>
    </div>
  );
}

export function ContactStep({ form, set, consents = false }) {
  const [locating, setLocating] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const upd = (k) => (e) => set((p) => ({ ...p, [k]: e.target.value }));

  const locate = () => {
    if (!navigator.geolocation) return toast.error("Geolocalizzazione non supportata dal browser");
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        set((p) => ({ ...p, lat: pos.coords.latitude, lng: pos.coords.longitude, geo_source: "gps" }));
        setLocating(false);
        toast.success("Posizione rilevata");
      },
      () => {
        setLocating(false);
        toast.error("Impossibile rilevare la posizione. Puoi cercarla dall'indirizzo.");
      },
      { enableHighAccuracy: true, timeout: 15000 },
    );
  };

  const geocode = async () => {
    const q = `${form.address} ${form.city}`.trim();
    if (q.length < 4) return toast.error("Inserisci indirizzo e comune");
    setGeocoding(true);
    try {
      const { data } = await api.get("/public/geo/search", { params: { q: form.address, city: form.city } });
      if (!data.length) toast.error("Indirizzo non trovato: usa Google Maps o tocca la mappa");
      else {
        set((p) => ({ ...p, lat: data[0].lat, lng: data[0].lng, geo_source: "indirizzo" }));
        toast.success("Coordinate trovate");
      }
    } catch {
      toast.error("Ricerca non disponibile");
    } finally {
      setGeocoding(false);
    }
  };

  const hasGeo = form.lat != null && form.lng != null;
  return (
    <div className="space-y-6">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field id="first_name" label="Nome *"><Input id="first_name" value={form.first_name} onChange={upd("first_name")} data-testid="first-name-input" /></Field>
        <Field id="last_name" label="Cognome *"><Input id="last_name" value={form.last_name} onChange={upd("last_name")} data-testid="last-name-input" /></Field>
        <Field id="email" label="Email *"><Input id="email" type="email" value={form.email} onChange={upd("email")} data-testid="email-input" /></Field>
        <Field id="phone" label="Telefono (WhatsApp) *"><Input id="phone" type="tel" value={form.phone} onChange={upd("phone")} placeholder="+39 333 1234567" data-testid="phone-input" /></Field>
        <Field id="city" label="Comune *">
          <ComuneInput value={form.city} onChange={(v) => set((p) => ({ ...p, city: v }))}
            onSelect={(c) => set((p) => ({ ...p, city: c.name, province: c.province, cap: c.cap }))} />
        </Field>
        <Field id="address" label="Indirizzo *">
          <StreetInput value={form.address} comune={form.city} onChange={(v) => set((p) => ({ ...p, address: v }))}
            onPick={(s) => set((p) => ({ ...p, address: [s.street || s.name, s.housenumber].filter(Boolean).join(" "),
              city: s.city || p.city, lat: s.lat, lng: s.lng, geo_source: "indirizzo" }))} />
        </Field>
        <Field id="tax_code" label="Codice fiscale (facoltativo)">
          <Input id="tax_code" value={form.tax_code || ""} maxLength={16} onChange={(e) => set((p) => ({ ...p, tax_code: e.target.value.toUpperCase().replace(/\s/g, "") }))}
            placeholder="es. RSSMRA80A01H501U" className="font-mono uppercase placeholder:normal-case placeholder:font-sans" data-testid="tax-code-input" />
        </Field>
      </div>

      <div className="rounded-2xl border bg-[#0f1710] p-5 text-white relative overflow-hidden grain">
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <MapPin className="mt-0.5 h-5 w-5 text-[#a2ca6b]" />
            <div>
              <p className="font-display font-semibold">Posizione dell'impianto</p>
              {hasGeo ? (
                <p className="text-sm text-[#a2ca6b] flex items-center gap-1.5" data-testid="geo-status">
                  <CheckCircle2 className="h-4 w-4" /> {form.lat.toFixed(5)}, {form.lng.toFixed(5)}
                </p>
              ) : (
                <p className="text-sm text-slate-300" data-testid="geo-status">Serve per proporti i giorni in cui un tecnico è già in zona.</p>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" onClick={locate} disabled={locating} data-testid="geolocate-button"
              className="rounded-full bg-[#a2ca6b] text-[#0f1710] hover:bg-[#b5d885]">
              {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <LocateFixed className="h-4 w-4" />} Usa la mia posizione
            </Button>
            <Button type="button" variant="outline" onClick={geocode} disabled={geocoding} data-testid="geocode-button"
              className="rounded-full border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white">
              {geocoding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Da indirizzo
            </Button>
          </div>
        </div>
        <div className="relative mt-4 rounded-xl bg-white p-3 text-slate-900">
          <LocationPicker lat={form.lat} lng={form.lng} address={form.address} city={form.city} onChange={(patch) => set((p) => ({ ...p, ...patch }))} />
        </div>
      </div>
      {consents && <Consents form={form} set={set} />}
    </div>
  );
}
